import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { ICarrier, ICarrierInput, carriersAtom, carriersLoadingAtom } from "./types";

type UseCarriers = [ICarrier[], {
    list: () => Promise<ICarrier[] | null>;
    create: (input: ICarrierInput) => Promise<ICarrier | null>;
    update: (id: number, input: ICarrierInput & { is_active?: boolean }) => Promise<ICarrier | null>;
    remove: (id: number) => Promise<ICarrier | null>;
}];

// #region useCarriers
export const useCarriers = (): UseCarriers => {
    const [items, setItems] = useAtom(carriersAtom);
    const setLoading = useSetAtom(carriersLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const headers = { 'X-Wallmates-Auth': authToken };

    // #region list
    const list = async (): Promise<ICarrier[] | null> => {
        try {
            setLoading(true);
            const response = await axios.get(`${environment.baseUrl}/carriers`, { headers });
            setItems(response.data);
            return response.data as ICarrier[];
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to fetch carriers.' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    // #region create
    const create = async (input: ICarrierInput): Promise<ICarrier | null> => {
        try {
            const response = await axios.post(`${environment.baseUrl}/carriers`, input, { headers });
            await list();
            return response.data as ICarrier;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to create carrier.' });
            return null;
        }
    };

    // #region update
    const update = async (id: number, input: ICarrierInput & { is_active?: boolean }): Promise<ICarrier | null> => {
        try {
            const response = await axios.put(`${environment.baseUrl}/carriers/${id}`, input, { headers });
            await list();
            return response.data as ICarrier;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update carrier.' });
            return null;
        }
    };

    // #region remove
    const remove = async (id: number): Promise<ICarrier | null> => {
        try {
            const response = await axios.delete(`${environment.baseUrl}/carriers/${id}`, { headers });
            await list();
            return response.data as ICarrier;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to delete carrier.' });
            return null;
        }
    };

    return [items, { list, create, update, remove }];
};
